import {Injectable} from '@angular/core';
import {BehaviorSubject, map} from 'rxjs';
import {ShellLoadFailureKind} from './shell-load-failure.util';

export type ClientLoadingReason =
    | 'auth-session-loading'
    | 'backend-unavailable'
    | 'rbac-tenant-denial'
    | 'route-loading';

export interface ClientLoadingState {
    isLoading: boolean;
    reason: ClientLoadingReason;
}

export type OidcCallbackDisposition = 'pending' | 'timed-out' | 'failed' | null;

export type ShellLoadFailureOwner = 'token-acquisition' | 'non-token' | null;

export interface ShellLoadFailureState {
    failure: ShellLoadFailureKind | null;
    owner: ShellLoadFailureOwner;
    epoch: number | null;
    interactiveRecoveryStarted: boolean;
}

/**
 * ClientStateService acts as a mediator between ClientService and ClientLoadingService
 * to break the circular dependency between them.
 *
 * This service maintains the state related to client loading and selection
 * that both services need to access.
 */
@Injectable({
    providedIn: 'root'
})
export class ClientStateService {
    // Subject to track client loading state
    private clientLoadingSubject = new BehaviorSubject<ClientLoadingState>({
        isLoading: true,
        reason: 'auth-session-loading'
    });
    public clientLoadingState$ = this.clientLoadingSubject.asObservable();
    public clientLoading$ = this.clientLoadingState$.pipe(map(state => state.isLoading));

    private betaBackendUnavailableSubject = new BehaviorSubject<boolean>(false);
    public betaBackendUnavailable$ = this.betaBackendUnavailableSubject.asObservable();

    private shellLoadFailureSubject = new BehaviorSubject<ShellLoadFailureKind | null>(null);
    public shellLoadFailure$ = this.shellLoadFailureSubject.asObservable();
    private shellLoadFailureState: ShellLoadFailureState = {
        failure: null,
        owner: null,
        epoch: null,
        interactiveRecoveryStarted: false
    };
    private nextTokenFailureEpoch = 0;
    private oidcCallbackDisposition: OidcCallbackDisposition = null;

    // Subject to notify when cache should be reset
    private resetCacheSubject = new BehaviorSubject<boolean>(false);
    public resetCache$ = this.resetCacheSubject.asObservable();

    constructor() {
    }

    /**
     * Updates the client loading state
     * @param isLoading Whether clients are currently loading
     */
    setClientLoading(isLoading: boolean, reason: ClientLoadingReason = this.clientLoadingSubject.value.reason): void {
        this.clientLoadingSubject.next({
            isLoading,
            reason
        });
    }

    getClientLoadingReason(): ClientLoadingReason {
        return this.clientLoadingSubject.value.reason;
    }

    setBetaBackendUnavailable(isUnavailable: boolean): void {
        this.betaBackendUnavailableSubject.next(isUnavailable);
    }

    getBetaBackendUnavailable(): boolean {
        return this.betaBackendUnavailableSubject.value;
    }

    setShellLoadFailure(failure: ShellLoadFailureKind | null): void {
        if (failure === null) {
            this.clearNonTokenShellLoadFailure();
            return;
        }

        if (this.shellLoadFailureState.owner === 'token-acquisition') {
            return;
        }

        this.shellLoadFailureState = {
            failure,
            owner: 'non-token',
            epoch: null,
            interactiveRecoveryStarted: false
        };
        this.shellLoadFailureSubject.next(failure);
    }

    getShellLoadFailure(): ShellLoadFailureKind | null {
        return this.shellLoadFailureSubject.value;
    }

    getShellLoadFailureState(): ShellLoadFailureState {
        return {...this.shellLoadFailureState};
    }

    clearNonTokenShellLoadFailure(): void {
        if (this.shellLoadFailureState.owner === 'token-acquisition') {
            return;
        }

        this.shellLoadFailureState = {
            failure: null,
            owner: null,
            epoch: null,
            interactiveRecoveryStarted: false
        };
        this.shellLoadFailureSubject.next(null);
    }

    recordTokenAcquisitionFailure(failure: ShellLoadFailureKind): ShellLoadFailureState {
        const existingTokenFailure = this.shellLoadFailureState.owner === 'token-acquisition'
            ? this.shellLoadFailureState
            : null;
        const epoch = existingTokenFailure?.epoch ?? ++this.nextTokenFailureEpoch;
        const stableFailure = existingTokenFailure?.failure ?? failure;

        this.shellLoadFailureState = {
            failure: stableFailure,
            owner: 'token-acquisition',
            epoch,
            interactiveRecoveryStarted: existingTokenFailure?.epoch === epoch
                && existingTokenFailure.interactiveRecoveryStarted
        };
        this.shellLoadFailureSubject.next(stableFailure);
        return this.getShellLoadFailureState();
    }

    clearTokenAcquisitionFailure(): void {
        if (this.shellLoadFailureState.owner !== 'token-acquisition') {
            return;
        }

        this.shellLoadFailureState = {
            failure: null,
            owner: null,
            epoch: null,
            interactiveRecoveryStarted: false
        };
        this.shellLoadFailureSubject.next(null);
    }

    markTokenInteractiveRecoveryStarted(epoch: number): boolean {
        if (this.shellLoadFailureState.owner !== 'token-acquisition'
            || this.shellLoadFailureState.epoch !== epoch
            || this.shellLoadFailureState.interactiveRecoveryStarted) {
            return false;
        }

        this.shellLoadFailureState = {
            ...this.shellLoadFailureState,
            interactiveRecoveryStarted: true
        };
        return true;
    }

    /**
     * Gets the current client loading state
     * @returns Current loading state
     */
    getClientLoading(): boolean {
        return this.clientLoadingSubject.value.isLoading;
    }

    setOidcCallbackDisposition(disposition: OidcCallbackDisposition): void {
        this.oidcCallbackDisposition = disposition;
    }

    getOidcCallbackDisposition(): OidcCallbackDisposition {
        return this.oidcCallbackDisposition;
    }

    /**
     * Triggers a cache reset notification
     */
    triggerCacheReset(): void {
        this.resetCacheSubject.next(true);
        // Reset back to false after emitting the event
        setTimeout(() => this.resetCacheSubject.next(false), 0);
    }
}
